import React, { useState } from 'react';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from '@heroui/react';
import { MoreVertical, Edit, Eye, EyeOff, Users, Building2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Course } from '@/services/apis/courses';
import ConfirmationModal from '../../base/ConfirmationModal';

interface CourseActionsDropdownProps {
  course: Course;
  onEdit: (course: Course) => void;
  onDelete: (course: Course) => void;
  onPublish: (course: Course) => void;
  onUnpublish: (course: Course) => void;
  onAssignUsers: (course: Course) => void;
  onAssignDepartments: (course: Course) => void;
  isPublishing: boolean;
  isUnpublishing: boolean;
  isDeleting: boolean;
}

const CourseActionsDropdown: React.FC<CourseActionsDropdownProps> = ({
  course,
  onEdit,
  onDelete,
  onPublish,
  onUnpublish,
  onAssignUsers,
  onAssignDepartments,
  isPublishing,
  isUnpublishing,
  isDeleting,
}) => {
  const { t } = useTranslation('admin');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const isPublished = course.isPublished;
  const canAssign = isPublished;

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(course);
    setDeleteModalOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  return (
    <>
      <Dropdown>
        <DropdownTrigger>
          <Button isIconOnly variant="light" size="sm" aria-label={t('courses.actions.moreActions')}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Course actions"
          variant="flat"
          disabledKeys={[
            ...(!canAssign ? ['assignUsers', 'assignDepartments'] : []),
            ...(isPublishing ? ['publish'] : []),
            ...(isUnpublishing ? ['unpublish'] : []),
            ...(isDeleting ? ['delete'] : []),
          ]}
        >
          <DropdownItem key="edit" startContent={<Edit className="w-4 h-4" />} onPress={() => onEdit(course)}>
            {t('courses.actions.edit')}
          </DropdownItem>

          {isPublished ? (
            <DropdownItem
              key="unpublish"
              startContent={<EyeOff className="w-4 h-4" />}
              onPress={() => onUnpublish(course)}
              color="warning"
            >
              {t('courses.actions.unpublish')}
            </DropdownItem>
          ) : (
            <DropdownItem
              key="publish"
              startContent={<Eye className="w-4 h-4" />}
              onPress={() => onPublish(course)}
              color="success"
            >
              {t('courses.actions.publish')}
            </DropdownItem>
          )}

          <DropdownItem
            key="assignUsers"
            startContent={<Users className="w-4 h-4" />}
            onPress={() => onAssignUsers(course)}
            description={!canAssign ? t('courses.actions.assignUsersDisabled') : undefined}
          >
            {t('courses.actions.assignUsers')}
          </DropdownItem>

          <DropdownItem
            key="assignDepartments"
            startContent={<Building2 className="w-4 h-4" />}
            onPress={() => onAssignDepartments(course)}
            description={!canAssign ? t('courses.actions.assignDepartmentsDisabled') : undefined}
          >
            {t('courses.actions.assignDepartments')}
          </DropdownItem>

          <DropdownItem
            key="delete"
            startContent={<Trash2 className="w-4 h-4" />}
            onPress={handleDeleteClick}
            color="danger"
          >
            {t('courses.actions.delete')}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('courses.modals.delete.title')}
        message={t('courses.modals.delete.message')}
        confirmLabel={t('courses.modals.delete.confirm')}
        cancelLabel={t('courses.modals.delete.cancel')}
        confirmColor="danger"
        isLoading={isDeleting}
      />
    </>
  );
};

export default CourseActionsDropdown;
